import { useLocation, useNavigate, Link } from 'react-router-dom'
import { ChevronLeft, LogOut, User, Bell, Megaphone, LifeBuoy } from 'lucide-react'
import { lazy, Suspense, useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useAuthStore } from '@/shared/stores/auth.store'
import { useLogout } from '@/shared/hooks/use-auth'
import { useOpenSupport } from '@/shared/hooks/use-open-support'
import { useAccess } from '@/shared/rbac/use-access'
import { canSearchClinical } from '@/shared/rbac/capabilities'
import { GlobalSearch } from '@/features/search/components/search-panel'
import { useSidebarStore } from '@/shared/stores/sidebar-store'
import { useBreadcrumbLabels, useHeaderBackOverride } from '@/shared/hooks/use-breadcrumb'
import { HelpMenu } from '@/features/walkthrough/components/help-menu'

const NotificationDropdown = lazy(
  () => import('@/features/notifications/components/notification-dropdown').then(m => ({ default: m.NotificationDropdown })),
)
const AnnouncementsDropdown = lazy(
  () => import('@/features/announcements/components/announcements-dropdown').then(m => ({ default: m.AnnouncementsDropdown })),
)

/** Route label mapping for breadcrumbs.
 *  Standard hierarchy: Medicodio › <Section> › <Page>.
 *  Dynamic id segments (`/clients/:id`) are resolved via useBreadcrumbLabels
 *  inside the Breadcrumb component below; everything static lives here. */
const routeLabels: Record<string, string> = {
  // Workspace
  '/workspace': 'Workspace',
  '/workspace/queue': 'Chart Queue',
  '/workspace/queue/create': 'Create Manual Chart',
  '/workspace/queue/dispatch': 'Dispatch',
  '/workspace/history': 'History',
  '/workspace/analytics': 'Analytics',

  // Charts Dashboard (route prefix kept as /client-admin)
  '/client-admin': 'Charts Dashboard',
  '/client-admin/overview': 'Overview',
  '/client-admin/pipeline': 'Pipeline',
  '/client-admin/exceptions': 'Exceptions',
  '/client-admin/settings': 'Settings',
  '/client-admin/facilities': 'Facilities',

  // Clients (Customer Success)
  '/clients': 'Clients',

  // Billing
  '/timesheet': 'Timesheet',
  '/invoicing': 'Invoicing',
  '/invoicing/invoices': 'Invoices',
  '/invoicing/imports': 'Hours imports',
  '/invoicing/import-workflows': 'Import workflows',

  // Manager pages — flat in breadcrumbs (see HIDDEN_BREADCRUMB_SEGMENTS).
  // Routes still live under /manager/* but the section label is suppressed
  // because every leaf already self-describes (Team, SLA, Notes, etc.) and
  // "Coding Manager" reads as redundant in breadcrumb context.
  '/manager/overview': 'Coding Manager Overview',
  '/manager/team': 'Team',
  '/manager/queue': 'Manager Queue',
  '/manager/sla': 'SLA',
  '/manager/escalations': 'Escalations',
  '/manager/overrides': 'Overrides',
  '/manager/workflow-rules': 'Workflow Rules',
  '/manager/audits': 'QA Audits',
  '/manager/notes': 'Team Notes',
  '/manager/analytics': 'Manager Analytics',

  // Compliance pages — flat in breadcrumbs (see HIDDEN_BREADCRUMB_SEGMENTS).
  // Audit trail / variance / overrides are cross-cutting, not module-owned,
  // so we surface them at the top level instead of nesting under "Compliance".
  '/compliance/overview': 'Compliance Overview',
  '/compliance/variance': 'Coding Variance',
  '/compliance/overrides': 'Overrides',

  // Revenue
  '/revenue': 'Revenue',
  '/revenue/overview': 'Overview',
  '/revenue/trends': 'Trends',

  // Appeals
  '/appeals': 'Appeals',
  '/appeals/list': 'Appeals',

  // Coder Performance Dashboard
  '/performance-dashboard': 'Coder Performance Dashboard',

  // Operations Dashboard
  '/operations-dashboard': 'Operations Dashboard',
  '/operations-dashboard/import': 'Import',

  // Billing Impact
  '/billing-impact': 'Billing Impact',
  '/billing-impact/summary': 'Billing Impact',
  '/billing-impact/charts': 'Matched Charts',
  '/billing-impact/uploads': 'Uploads',

  // Analytics Dashboard (route prefix kept as /engine-admin)
  '/engine-admin': 'Analytics Dashboard',
  '/engine-admin/overview': 'Overview',
  '/engine-admin/runs': 'Run History',
  '/engine-admin/feedback': 'Feedback',
  '/engine-admin/costs': 'Costs',
  '/engine-admin/flag-metrics': 'Flag Metrics',
  '/engine-admin/audit-trail': 'Audit Trail',
  '/engine-admin/notifications': 'Notification Preferences',
  '/engine-admin/settings': 'Settings',

  // Prompt Library
  '/prompt-config': 'Prompt Library',
  '/prompt-config/workflows': 'Workflows',
  '/prompt-config/setup': 'Workflow Setup',
  '/prompt-config/configurations': 'Configurations',
  '/prompt-config/bundle': 'Bundle Editor',
  '/prompt-config/create': 'New Bundle',
  '/prompt-config/parameters': 'Prompt Parameters',
  '/prompt-config/sections': 'Prompt Sections',

  // Platform Config
  '/platform-config': 'Platform Config',
  '/platform-config/mandatory-fields': 'Mandatory Fields',

  // Client Config (modules + submodules + per-client overrides)
  '/client-config': 'Configurations',
  '/client-config/prediction': 'Prediction',
  '/client-config/prediction/modules': 'Modules',
  '/client-config/prediction/registry': 'Submodules',
  '/client-config/prediction/settings': 'Client Configuration',
  '/client-config/integration': 'Integration',
  '/client-config/integration/failure-reasons': 'Failure Reasons',
  '/client-config/integration/generic-chart-fields': 'Generic Chart Fields',
  '/client-config/integration/chart-type-fields': 'Chart Type Fields',
  '/client-config/integration/client-chart-field-mappings': 'Client Field Mappings',
  '/client-config/system-config': 'System Config',
  '/client-config/model-pricing': 'Model Pricing',

  // System Admin
  '/system-admin': 'System Admin',
  '/system-admin/clients': 'Clients & Facilities',
  '/system-admin/facilities': 'Facilities',
  '/system-admin/providers': 'Providers',
  '/system-admin/users': 'Users',
  '/system-admin/roles': 'Roles',
  '/system-admin/encounters': 'Encounters',
  '/system-admin/query-policy': 'Query Policy',
  '/system-admin/query-templates-master': 'Query Templates',
  '/system-admin/query-health': 'Query Health',
  '/system-admin/audit-logs': 'Audit Log',
  '/system-admin/audit-trail': 'Audit Trail',

  // Knowledge Base
  '/kb': 'Knowledge Base',
  '/kb/cpt': 'CPT Codes',
  '/kb/icd': 'ICD-10 Codes',
  '/kb/pcs': 'ICD-10-PCS Codes',
  '/kb/hcpcs': 'HCPCS Codes',
  '/kb/modifiers': 'Modifiers',
  '/kb/mue': 'Medically Unlikely Edits (MUE)',
  '/kb/report-types': 'Report Types',
  '/kb/care-settings': 'Care Settings',
  '/kb/ncci': 'NCCI Edits',
  '/kb/combinations': 'Code Combinations',
  '/kb/coverage': 'Coverage',
  '/kb/encounters': 'Encounters',
  '/kb/enm': 'Evaluation & Management (E&M) Rules',
  '/kb/guidelines': 'Guidelines',
  '/kb/main-diagnosis': 'Main Diagnosis',
  '/kb/payers': 'Payers',
  '/kb/specialties': 'Specialties',
  '/kb/pfs-rvu': 'Physician Fee Schedule — Relative Value Units (RVU)',
  '/kb/msdrg': 'Medicare Severity Diagnosis-Related Groups (MS-DRG)',
  '/kb/ipps-wage-index': 'Inpatient Prospective Payment System (IPPS) Wage Index',
  '/kb/drg-grouper': 'DRG Grouper',
  '/kb/imports': 'Imports',
  '/kb/audit': 'Audit Trail',

  // Announcements (internal module key/slug stays `updates`; only the route path is /announcements)
  '/announcements': 'Announcements',
  '/announcements/timeline': "What's New",
  '/announcements/admin': 'Compose Announcements',

  // Notifications
  '/notifications': 'Notifications',

  // Settings
  '/settings': 'Settings',
  '/settings/profile': 'Profile',
  '/settings/security': 'Security',
  '/settings/sessions': 'Sessions',
  '/settings/notifications': 'Notification Preferences',

  // Access Request & Break-Glass
  '/access': 'Access Requests',
  '/access/requests': 'Requests',
  '/access/new': 'New Request',
}

/** Section prefixes that should NOT render as breadcrumb ancestors.
 *  Routes still live under these paths (manager/notes, compliance/audit-trail)
 *  but the section label gets suppressed in the trail because the leaf page
 *  already self-describes and the section name reads as redundant. */
const HIDDEN_BREADCRUMB_SEGMENTS = new Set<string>(['/manager', '/compliance'])

export function Header() {
  const { collapsed } = useSidebarStore()
  // Global search only ever returns clinical things — codes, patients,
  // providers, facilities, encounters. A finance role (biller) holds neither
  // chart_queue nor the KB, so the box returns medical codes it has no use for
  // and zero of everything else. Don't offer a control that cannot work.
  // (Chart filters and ID lists inside the panel additionally need chart read.)
  const canSearchAnything = canSearchClinical(useAccess())

  return (
    <header className={`header${collapsed ? ' sidebar-collapsed' : ''}`}>
      <Breadcrumb />
      {/* The search field + its anchored results dropdown (Cmd/Ctrl+K). Icon only below 640px. */}
      {canSearchAnything && <GlobalSearch />}
      <HeaderUtilities />
    </header>
  )
}

function Breadcrumb() {
  const location = useLocation()
  const navigate = useNavigate()
  const dynamicLabels = useBreadcrumbLabels()
  // Pages with an internal drill-down (e.g. KB ICD hierarchy chapter view)
  // register an override so back steps out of the drill-down first.
  const backOverride = useHeaderBackOverride()
  const segments = location.pathname.split('/').filter(Boolean)

  /** Known tail segment labels for encounter sub-pages and tab routes */
  const tailLabels: Record<string, string> = {
    chart: 'Chart',
    review: 'Review',
    'em-results': 'E&M Results',
    'ncci-results': 'NCCI Results',
    dispatch: 'Dispatch',
    config: 'Configuration',
    permissions: 'Permissions',
    new: 'New',
  }

  const crumbs: { label: string; path: string }[] = []
  let currentPath = ''
  for (const segment of segments) {
    currentPath += `/${segment}`
    // Skip section-only prefixes that we explicitly hide from the trail.
    if (HIDDEN_BREADCRUMB_SEGMENTS.has(currentPath)) continue
    // Dynamic labels (set by the active page) override static labels so a
    // tabbed page can update the crumb when the user switches tab. Static
    // labels remain the fallback; tail labels handle bare dynamic segments.
    const label = dynamicLabels.get(currentPath) ?? routeLabels[currentPath] ?? tailLabels[segment]
    if (label) {
      crumbs.push({ label, path: currentPath })
    }
  }

  // Show back on 3+ deep pages OR on any KB / Clients sub-page
  // (/kb/cpt, /clients/:id, /clients/:id/contracts, etc.).
  // Access pages render their own in-section back link, so suppress the
  // breadcrumb back button there to avoid two back controls.
  const showBack =
    segments[0] !== 'access'
    && (
      segments.length > 2
      || (segments.length > 1 && (segments[0] === 'kb' || segments[0] === 'clients'))
    )

  // /clients/:id/<tab> back-target collapses to /clients (skipping the bare
  // /clients/:id route which now just redirects to /:id/details).
  const isClientsTab =
    segments.length === 3 && segments[0] === 'clients'
  const backTarget = isClientsTab
    ? '/clients'
    : '/' + segments.slice(0, -1).join('/')

  return (
    <div className="header-breadcrumb">
      {showBack && (
        <button
          onClick={() => (backOverride ? backOverride() : navigate(backTarget))}
          className="header-icon-btn"
          style={{ width: 32, height: 32 }}
          aria-label="Go back"
        >
          <ChevronLeft size={18} />
        </button>
      )}
      <Link to="/" className="breadcrumb-link">Medicodio</Link>
      {crumbs.map((crumb, i) => (
        <span key={crumb.path} style={{ display: 'contents' }}>
          <span className="breadcrumb-sep">›</span>
          {i === crumbs.length - 1 ? (
            <span className="breadcrumb-current">{crumb.label}</span>
          ) : (
            <Link to={crumb.path} className="breadcrumb-link">
              {crumb.label}
            </Link>
          )}
        </span>
      ))}
    </div>
  )
}

function HeaderUtilities() {
  const { user } = useAuthStore()
  const logout = useLogout()
  const { canViewAnnouncements } = useAccess()
  const { openSupport, pending: supportPending } = useOpenSupport()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  // Separate ref for the portal'd dropdown card. Because the dropdown lives
  // under <body> (not inside menuRef), the outside-click handler needs to
  // treat clicks inside this ref as "inside" too — otherwise Profile / Sign
  // Out close the menu before the link/button activates.
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Measure the real header's right/bottom at the moment the dropdown opens
  // so the dropdown's right-edge and top-gap match the header card pixel for
  // pixel — independent of header-height/border/shadow drift between CSS
  // values and rendered box.
  const [anchor, setAnchor] = useState<{ top: number; right: number } | null>(null)
  useEffect(() => {
    if (!menuOpen) { setAnchor(null); return }
    function measure() {
      const headerEl = document.querySelector('.header') as HTMLElement | null
      if (!headerEl) return
      const rect = headerEl.getBoundingClientRect()
      const screenGapRight = window.innerWidth - rect.right
      setAnchor({
        // Same gap below the header card as the header card has from screen right.
        top: rect.bottom + screenGapRight,
        right: screenGapRight,
      })
    }
    measure()
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, true)
    return () => {
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
    }
  }, [menuOpen])

  useEffect(() => {
    if (!menuOpen) return
    function handleClick(e: MouseEvent) {
      const target = e.target as Node
      const insideAvatar = menuRef.current?.contains(target) ?? false
      const insideDropdown = dropdownRef.current?.contains(target) ?? false
      if (!insideAvatar && !insideDropdown) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [menuOpen])

  const initials = user
    ? `${user.first_name[0]}${user.last_name[0]}`
    : '??'

  return (
    <div className="header-actions">
      <HelpMenu />
      {canViewAnnouncements && (
        <Suspense fallback={<button className="header-icon-btn" aria-label="Announcements"><Megaphone size={18} /></button>}>
          <AnnouncementsDropdown />
        </Suspense>
      )}

      <Suspense fallback={<button className="header-icon-btn" aria-label="Notifications"><Bell size={18} /></button>}>
        <NotificationDropdown />
      </Suspense>

      <div ref={menuRef} style={{ position: 'relative' }}>
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="header-avatar"
          data-tour="header.user-menu"
          aria-label={`User menu: ${user?.first_name ?? ''} ${user?.last_name ?? ''}`}
          aria-expanded={menuOpen}
          aria-haspopup="true"
        >
          {initials}
        </button>

        {menuOpen && anchor && createPortal(
          <div
            ref={dropdownRef}
            className="card"
            style={{
              // Portal'd to <body> + position values measured from the actual
              // rendered header card. Same `screenGapRight` is used for both
              // `right` and the gap above the dropdown, so the dropdown's
              // visible gap from the screen edge and from the header bottom
              // match pixel-perfectly regardless of CSS-var values.
              position: 'fixed',
              right: anchor.right,
              top: anchor.top,
              width: 192,
              padding: 0,
              zIndex: 'var(--z-modal)',
              boxShadow: 'var(--shadow-lg)',
              overflow: 'hidden',
              borderRadius: 'var(--r-lg)',
              animation: 'scale-in 0.15s var(--ease-out) both',
            }}
            role="menu"
          >
            <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>
                {user?.first_name} {user?.last_name}
              </div>
              <div className="text-label" style={{ marginTop: 1, fontSize: 10, letterSpacing: '0.04em' }}>
                {user?.roles[0]?.name ?? ''}
              </div>
            </div>

            <div style={{ padding: '3px 0' }}>
              {user?.features?.support_codio && (
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); void openSupport() }}
                  disabled={supportPending}
                  style={{ ...MENU_ITEM_STYLE, width: '100%', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-sans)' }}
                  onMouseEnter={menuItemHoverIn}
                  onMouseLeave={menuItemHoverOut}
                  role="menuitem"
                >
                  <LifeBuoy size={12} />
                  Support
                </button>
              )}
              <MenuLink to="/settings/profile" icon={<User size={12} />} label="Profile" onClick={() => setMenuOpen(false)} />
            </div>

            <div style={{ borderTop: '1px solid var(--border-subtle)', padding: '3px 0' }}>
              <button
                onClick={() => { logout(); setMenuOpen(false) }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  width: '100%',
                  padding: '6px 14px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 500,
                  fontFamily: 'var(--font-sans)',
                  color: 'var(--danger)',
                  transition: 'background var(--motion-fast) var(--ease-out)',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--danger-bg)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                role="menuitem"
              >
                <LogOut size={12} />
                Sign Out
              </button>
            </div>
          </div>,
          document.body,
        )}
      </div>
    </div>
  )
}

const MENU_ITEM_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 14px',
  fontSize: 12,
  fontWeight: 500,
  color: 'var(--text-secondary)',
  textDecoration: 'none',
  transition: 'background var(--motion-fast) var(--ease-out), color var(--motion-fast) var(--ease-out)',
}

function menuItemHoverIn(e: React.MouseEvent<HTMLElement>) {
  e.currentTarget.style.background = 'var(--surface-0)'
  e.currentTarget.style.color = 'var(--text-primary)'
}

function menuItemHoverOut(e: React.MouseEvent<HTMLElement>) {
  e.currentTarget.style.background = 'transparent'
  e.currentTarget.style.color = 'var(--text-secondary)'
}

function MenuLink({
  to,
  icon,
  label,
  onClick,
}: {
  to: string
  icon: React.ReactNode
  label: string
  onClick: () => void
}) {
  return (
    <Link
      to={to}
      onClick={onClick}
      style={MENU_ITEM_STYLE}
      onMouseEnter={menuItemHoverIn}
      onMouseLeave={menuItemHoverOut}
      role="menuitem"
    >
      {icon}
      {label}
    </Link>
  )
}

